import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Loader2, Eye, EyeOff, ChevronRight, ChevronLeft, Shield, Zap, Users, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { GoldButton, Logo, inputClass, labelClass } from "../components/shared/UIComponents";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY_TYPES = [
  { value: "bank", label: "Bank" }, { value: "credit_union", label: "Credit Union" },
  { value: "private_fund", label: "Private Fund" }, { value: "hedge_fund", label: "Hedge Fund" },
  { value: "family_office", label: "Family Office" }, { value: "other", label: "Other" },
];
const AUM_RANGES = ["Under $1M", "$1M-$10M", "$10M-$50M", "$50M-$100M", "$100M+"];
const LOAN_TYPES = [
  { value: "fix_flip", label: "Fix & Flip" }, { value: "bridge", label: "Bridge" },
  { value: "dscr", label: "DSCR" }, { value: "cash_out", label: "Cash-Out Refi" },
  { value: "construction", label: "Construction" }, { value: "ground_up", label: "Ground Up" },
  { value: "multifamily", label: "Multifamily" }, { value: "commercial", label: "Commercial" },
];
const VALUE_PROPS = [
  { icon: Zap, title: "Fast Matching", text: "AI-powered lender matching in minutes" },
  { icon: Shield, title: "Vetted Partners", text: "Every lender on our platform is verified" },
  { icon: Users, title: "Direct Access", text: "Connect with institutional capital partners" },
];

export default function AuthPage({ onLogin }) {
  const { navigateTo } = usePageNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot | verification_sent | reset_sent
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", role: "borrower",
    company: "", phone: "", location: "", nmls_id: "",
    company_type: "", aum: "", lending_states: "",
    loan_types: [], min_loan: "", max_loan: "",
  });
  const [lenderStep, setLenderStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [resending, setResending] = useState(false);
  const { login } = useAuth();

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isLender = form.role === "lender" || form.role === "investor";

  const toggleLoanType = (val) => {
    set("loan_types", form.loan_types.includes(val) ? form.loan_types.filter(t => t !== val) : [...form.loan_types, val]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "register" && isLender && lenderStep === 1) { setLenderStep(2); return; }
    if (mode === "register" && !agreedToTerms) { setError("You must agree to the Terms of Service and Privacy Policy"); return; }
    setLoading(true); setError(null);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        if (onLogin) onLogin(u);
      } else {
        const payload = { full_name: form.full_name, email: form.email, password: form.password, role: form.role };
        if (isLender) {
          Object.assign(payload, {
            company: form.company, phone: form.phone || null, location: form.location || null,
            nmls_id: form.nmls_id || null, company_type: form.company_type, aum: form.aum || null,
            lending_states: form.lending_states || null,
            loan_types: form.loan_types.length > 0 ? form.loan_types : null,
            min_loan: form.min_loan ? Number(form.min_loan) : null, max_loan: form.max_loan ? Number(form.max_loan) : null,
          });
        }
        const res = await axios.post(`${API}/auth/register`, payload);
        setVerificationEmail(form.email);
        setMode("verification_sent");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong");
    }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(forgotEmail)}`);
      setMode("reset_sent");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(verificationEmail)}`);
    } catch (err) { /* silent */ }
    finally {
      setTimeout(() => setResending(false), 3000);
    }
  };

  const emailPwFields = (suffix = "") => (
    <>
      <label className="block">
        <span className={labelClass}>Email</span>
        <input required type="email" value={form.email} onChange={e => set("email", e.target.value)}
          className={inputClass} placeholder="you@email.com" data-testid={`auth-email${suffix}`} />
      </label>
      <label className="block">
        <span className={labelClass}>Password</span>
        <div className="relative">
          <input required type={showPw ? "text" : "password"} value={form.password}
            onChange={e => set("password", e.target.value)} minLength={8}
            className={inputClass + " pr-12"} placeholder="Min 8 characters" data-testid={`auth-password${suffix}`} />
          <button type="button" onClick={() => setShowPw(!showPw)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-cream/40 hover:text-cream/70">
            {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
      </label>
    </>
  );

  // Verification sent screen
  if (mode === "verification_sent") {
    return (
      <div className="relative min-h-[85vh] flex items-center justify-center" data-testid="verification-sent-page">
        <div className="absolute inset-0 bg-emerald-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(229,201,139,0.06),transparent_60%)]" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 text-center max-w-md px-4 sm:px-6">
          <div className="mx-auto h-20 w-20 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mb-6">
            <Mail className="h-10 w-10 text-gold" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Check Your Email</h2>
          <p className="text-sm text-cream/60 leading-relaxed mb-6">
            We've sent a verification link to <strong className="text-gold">{verificationEmail}</strong>. Click the link to verify your account and get started.
          </p>
          <div className="space-y-3">
            <button onClick={handleResendVerification} disabled={resending} data-testid="resend-verification-btn"
              className="w-full py-3 rounded-xl border border-emerald-700/30 text-cream/60 text-sm font-medium hover:text-cream transition-colors disabled:opacity-50">
              {resending ? "Sent!" : "Resend Verification Email"}
            </button>
            <button onClick={() => { setMode("login"); setError(null); }} data-testid="back-to-login-btn"
              className="w-full py-3 rounded-xl text-gold/70 text-sm font-medium hover:text-gold transition-colors flex items-center justify-center gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to Sign In
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Reset link sent screen
  if (mode === "reset_sent") {
    return (
      <div className="relative min-h-[85vh] flex items-center justify-center" data-testid="reset-sent-page">
        <div className="absolute inset-0 bg-emerald-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(229,201,139,0.06),transparent_60%)]" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 text-center max-w-md px-4 sm:px-6">
          <div className="mx-auto h-20 w-20 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mb-6">
            <CheckCircle className="h-10 w-10 text-gold" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Reset Link Sent</h2>
          <p className="text-sm text-cream/60 leading-relaxed mb-6">
            If an account exists for <strong className="text-gold">{forgotEmail}</strong>, you'll receive a password reset link shortly. The link expires in 1 hour.
          </p>
          <button onClick={() => { setMode("login"); setError(null); }} data-testid="back-to-login-from-reset"
            className="w-full py-3 rounded-xl text-gold/70 text-sm font-medium hover:text-gold transition-colors flex items-center justify-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Back to Sign In
          </button>
        </motion.div>
      </div>
    );
  }

  // Forgot password form
  if (mode === "forgot") {
    return (
      <div className="relative min-h-[85vh] flex items-center justify-center" data-testid="forgot-password-page">
        <div className="absolute inset-0 bg-emerald-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(229,201,139,0.06),transparent_60%)]" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md px-4 sm:px-6">
          <div className="text-center mb-8">
            <Logo size="small" showTagline={false} />
            <h2 className="text-2xl font-bold text-white mt-6">Reset Password</h2>
            <p className="text-sm text-cream/50 mt-2">Enter your email and we'll send you a reset link.</p>
          </div>
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <label className="block">
              <span className={labelClass}>Email Address</span>
              <input required type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                className={inputClass} placeholder="you@email.com" data-testid="forgot-email" />
            </label>
            {error && <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}
            <GoldButton type="submit" disabled={loading} className="w-full !rounded-xl" data-testid="forgot-submit">
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Sending...</span> : "Send Reset Link"}
            </GoldButton>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => { setMode("login"); setError(null); }} data-testid="forgot-back-to-login"
              className="text-sm text-gold/70 hover:text-gold transition-colors flex items-center justify-center gap-2 mx-auto">
              <ArrowLeft className="h-4 w-4" /> Back to Sign In
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[85vh] flex" data-testid="auth-page">
      {/* Left Branding Panel */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80"
            alt="Modern architecture" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-emerald-950/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-transparent to-emerald-950/60" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Logo size="default" showTagline={false} />
          <div className="space-y-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <h2 className="text-3xl font-light tracking-tight text-white leading-snug">
                The Lending Marketplace<br />
                <span className="font-bold text-gold">Built for Investors</span>
              </h2>
              <p className="mt-4 text-base text-cream/60 leading-relaxed max-w-md">
                Access structured capital, connect with vetted lenders, and close deals faster through our institutional-grade platform.
              </p>
            </motion.div>
            <div className="space-y-4">
              {VALUE_PROPS.map((vp, i) => {
                const Icon = vp.icon;
                return (
                  <motion.div key={vp.title} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="flex items-center gap-4 p-3 rounded-xl border border-emerald-800/50 bg-emerald-900/30 backdrop-blur-sm">
                    <div className="h-10 w-10 rounded-lg bg-emerald-800/50 border border-emerald-700/50 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{vp.title}</p>
                      <p className="text-xs text-cream/50">{vp.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
          <p className="text-xs text-cream/25 mt-8">Capital &bull; Growth &bull; Legacy</p>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-emerald-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(229,201,139,0.06),transparent_60%)]" />

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          className={`relative z-10 w-full mx-auto px-8 ${mode === "register" && isLender && lenderStep === 2 ? "max-w-lg" : "max-w-md"}`}>
          <div className="lg:hidden text-center mb-8"><Logo size="small" showTagline={false} /></div>
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white" data-testid="auth-title">
              {mode === "login" ? "Welcome Back" : lenderStep === 2 ? "Lender Details" : "Create Account"}
            </h2>
            <p className="mt-2 text-sm text-cream/50">
              {mode === "login" ? "Sign in to access your dashboard" : lenderStep === 2 ? "Tell us about your lending business" : "Join the Imaani Capital marketplace"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            <AnimatePresence mode="wait">
              {mode === "register" && lenderStep === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-5">
                  <label className="block"><span className={labelClass}>Full Name</span>
                    <input required value={form.full_name} onChange={e => set("full_name", e.target.value)} className={inputClass} placeholder="Your full name" data-testid="auth-name" /></label>
                  <label className="block"><span className={labelClass}>I am a...</span>
                    <div className="grid grid-cols-2 gap-3">
                      {[{ value: "borrower", label: "Borrower" }, { value: "lender", label: "Lender / Investor" }].map(opt => (
                        <button key={opt.value} type="button" onClick={() => { set("role", opt.value); setLenderStep(1); }} data-testid={`role-${opt.value}`}
                          className={`rounded-xl border px-4 py-3 text-sm font-bold transition-all ${form.role === opt.value ? "border-gold bg-gold/20 text-gold" : "border-emerald-800/30 bg-emerald-950/50 text-cream/60 hover:border-emerald-700/50"}`}>{opt.label}</button>
                      ))}
                    </div>
                  </label>
                </motion.div>
              )}
              {mode === "register" && isLender && lenderStep === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><span className={labelClass}>Company *</span><input required value={form.company} onChange={e => set("company", e.target.value)} className={inputClass} placeholder="Your firm" data-testid="auth-company" /></label>
                    <label className="block"><span className={labelClass}>Phone</span><input value={form.phone} onChange={e => set("phone", e.target.value)} className={inputClass} placeholder="+1 (555) 000-0000" data-testid="auth-phone" /></label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><span className={labelClass}>Company Type *</span>
                      <select required value={form.company_type} onChange={e => set("company_type", e.target.value)} className={inputClass} data-testid="auth-company-type">
                        <option value="">Select type...</option>{COMPANY_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}</select></label>
                    <label className="block"><span className={labelClass}>NMLS ID</span><input value={form.nmls_id} onChange={e => set("nmls_id", e.target.value)} className={inputClass} placeholder="Optional" data-testid="auth-nmls" /></label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><span className={labelClass}>AUM Range</span>
                      <select value={form.aum} onChange={e => set("aum", e.target.value)} className={inputClass} data-testid="auth-aum">
                        <option value="">Select...</option>{AUM_RANGES.map(r => <option key={r} value={r}>{r}</option>)}</select></label>
                    <label className="block"><span className={labelClass}>Location</span><input value={form.location} onChange={e => set("location", e.target.value)} className={inputClass} placeholder="City, State" data-testid="auth-location" /></label>
                  </div>
                  <label className="block"><span className={labelClass}>Lending States</span><input value={form.lending_states} onChange={e => set("lending_states", e.target.value)} className={inputClass} placeholder="TX, FL, CA" data-testid="auth-states" /></label>
                  <div><span className={labelClass}>Loan Programs</span>
                    <div className="flex gap-2 flex-wrap mt-1">
                      {LOAN_TYPES.map(lt => (
                        <button key={lt.value} type="button" onClick={() => toggleLoanType(lt.value)} data-testid={`auth-loantype-${lt.value}`}
                          className={`px-3 py-2 rounded-lg border text-xs font-bold transition-all ${form.loan_types.includes(lt.value) ? "border-gold bg-gold/20 text-gold" : "border-emerald-800/30 text-cream/50 hover:border-emerald-700/50"}`}>{lt.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><span className={labelClass}>Min Loan ($)</span><input type="number" value={form.min_loan} onChange={e => set("min_loan", e.target.value)} className={inputClass} placeholder="100,000" data-testid="auth-min-loan" /></label>
                    <label className="block"><span className={labelClass}>Max Loan ($)</span><input type="number" value={form.max_loan} onChange={e => set("max_loan", e.target.value)} className={inputClass} placeholder="5,000,000" data-testid="auth-max-loan" /></label>
                  </div>
                  <button type="button" onClick={() => setLenderStep(1)} className="flex items-center gap-1 text-sm text-gold/70 hover:text-gold transition-colors" data-testid="auth-back-btn"><ChevronLeft className="h-4 w-4" /> Back</button>
                </motion.div>
              )}
            </AnimatePresence>

            {!(mode === "register" && isLender && lenderStep === 2) && emailPwFields()}
            {mode === "register" && isLender && lenderStep === 2 && emailPwFields("-step2")}

            {mode === "register" && !(isLender && lenderStep === 1) && (
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="terms-checkbox-label">
                <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}
                  data-testid="terms-checkbox"
                  className="mt-1 h-4 w-4 rounded border-emerald-700/50 bg-emerald-950/50 text-gold accent-gold focus:ring-gold/30 shrink-0" />
                <span className="text-xs text-cream/60 leading-relaxed">
                  I agree to the{" "}
                  <button type="button" onClick={() => navigateTo("terms")} className="text-gold hover:text-gold/80 underline underline-offset-2">Terms of Service</button>
                  {" "}and{" "}
                  <button type="button" onClick={() => navigateTo("privacy")} className="text-gold hover:text-gold/80 underline underline-offset-2">Privacy Policy</button>
                </span>
              </label>
            )}

            {error && <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="auth-error">{error}</div>}

            <GoldButton type="submit" disabled={loading || (mode === "register" && !(isLender && lenderStep === 1) && !agreedToTerms)} className="w-full !rounded-xl" data-testid="auth-submit">
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Processing...</span>
                : mode === "login" ? "Sign In"
                : mode === "register" && isLender && lenderStep === 1 ? <span className="flex items-center justify-center gap-2">Continue <ChevronRight className="h-4 w-4" /></span>
                : isLender ? "Submit for Approval" : "Create Account"}
            </GoldButton>
            {isLender && mode === "register" && lenderStep === 2 && (
              <p className="text-xs text-cream/40 text-center">Your account will be reviewed by our team before access is granted.</p>
            )}
          </form>

          {/* Forgot Password Link */}
          {mode === "login" && (
            <div className="mt-4 text-center">
              <button onClick={() => { setMode("forgot"); setError(null); setForgotEmail(form.email); }} data-testid="forgot-password-link"
                className="text-xs text-cream/40 hover:text-gold/70 transition-colors">
                Forgot your password?
              </button>
            </div>
          )}

          <div className="mt-4 text-center">
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); setLenderStep(1); setAgreedToTerms(false); }}
              className="text-sm text-gold/70 hover:text-gold transition-colors" data-testid="auth-toggle">
              {mode === "login" ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
